import * as stylex from "@stylexjs/stylex";
import { ChevronDown, ChevronUp, CloudRain, FileText, Plus } from "lucide-react";
import { copy } from "@/copy";
import { type DayListGroups, type DayListRow, dayListGroups } from "@/domain/dayList";
import type { CalendarEvent, Tag } from "@/domain/schemas";
import { eventColor, whoLabel } from "@/domain/tags";
import { atMinute, type Clock, formatClock, formatDuration, rangeParts } from "@/domain/time";
import { planRain } from "@/domain/timeline";
import { hourlyByHour } from "@/domain/weather";
import "@/theme/seasons.stylex";
import { season } from "@/theme/seasons.stylex";
import "@/theme/tokens.stylex";
import { colors, motion, radii } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import type { TimelineFree, TimelineWeather } from "@/ui/DayTimeline";
import { Icon } from "@/ui/Icon";
import { PersonDot } from "@/ui/PersonDot";
import { press } from "@/ui/press";
import { tagFills } from "@/ui/tagFills";
type Slot = {
  start: Date;
  end: Date;
};
type DayListProps = {
  events: readonly CalendarEvent[];
  tags: readonly Tag[];
  day: string;
  now: Date;
  tz: string;
  clock: Clock;
  free: TimelineFree;
  weather?: TimelineWeather;
  earlierOpen: boolean;
  onToggleEarlier: () => void;
  onOpenEvent: (event: CalendarEvent) => void;
  onAddEvent: (slot: Slot) => void;
};
type Group = {
  key: string;
  head?: string;
  rows: readonly DayListRow<CalendarEvent>[];
};
type Shared = Pick<DayListProps, "tags" | "day" | "tz" | "clock" | "free" | "onOpenEvent" | "onAddEvent"> & {
  rain: ReadonlyMap<number, number>;
};
const OPEN_PLAN_LENGTH = 30;
const DOTS_MAX = 4;
const UNBOUNDED = Number.POSITIVE_INFINITY;
const styles = {
  toggle: {
    kSGwAc: "xqcrz7y",
    kAzted: "x1gg8mnh",
    kMzoRj: "xc342km",
    kaIpWk: "xqhrb7l",
    kWkggS: "x5r8ks9",
    kMwMTN: "x1a11ngh",
    k8WAf4: "xt970qd",
    kZCmMZ: "x1onr9mi",
    kwRFfy: "x1x5flf6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x167g77z",
    kMv6JI: "xjb2p0i",
    kGuDYH: "x1jvydc1",
    k63SB2: "x1cf7cre",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  tile: {
    kzqmXN: "xh8yej3",
    kB7OPa: "x9f619",
    kMzoRj: "xc342km",
    kaIpWk: "x1fv5t7x",
    kVVagm: "xln7xf2",
    kMwMTN: "x1a11ngh",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x11xpdln",
    kIyJzY: "x1bpocu2",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  allDay: {
    kAzted: "x1wiwyrm",
    k8WAf4: "xp59q4u",
    kg3NbH: "xqin4a2",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1v2ro7d",
    kWkggS: "x5r8ks9",
    $$css: true
  },
  plan: {
    kLKAdn: "x889kno",
    kg3NbH: "xaope02",
    kGO01o: "xsag5q8",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1jnr06f",
    $$css: true
  },
  soft: {
    kWkggS: "x1hueysq",
    $$css: true
  },
  past: {
    kWkggS: "xh540kj",
    kGVxlE: "x98nf4m",
    $$css: true
  },
  now: {
    kGVxlE: "xuamy0h",
    $$css: true
  },
  free: {
    kAzted: "x1gg8mnh",
    k8WAf4: "xt970qd",
    kg3NbH: "xaope02",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x167g77z",
    kMzoRj: "xdh2fpr",
    ksu8eU: "xbsl7fq",
    kVAM5u: "x1ky62hi",
    kWkggS: "xjbqb8w",
    $$css: true
  }
};
export function DayList({
  events,
  tags,
  day,
  now,
  tz,
  clock,
  free,
  weather,
  earlierOpen,
  onToggleEarlier,
  onOpenEvent,
  onAddEvent
}: DayListProps) {
  const allDay = events.filter(event => event.allDay);
  const groups = dayListGroups(events, {
    day,
    now,
    tz
  }, free !== "none");
  const rain = weather ? rainByHour(weather, day, tz) : new Map<number, number>();
  const shared: Shared = {
    tags,
    day,
    tz,
    clock,
    free,
    rain,
    onOpenEvent,
    onAddEvent
  };
  const message = messageFor(free, events.length);
  const ended = groups.kind === "today" ? groups.earlier : [];
  return <div className="x78zum5 xdt5ytf x1665zp3 xeuugli">
      {message && <p className="x1ghz6dp x19d36u7 x15i68ds xfrs9s4 x1fzhlzt">{message}</p>}
      {allDay.length > 0 && <div className="x78zum5 xdt5ytf x167g77z xeuugli">
          {allDay.map(event => <AllDayRow key={event.id} event={event} tags={tags} onOpen={onOpenEvent} />)}
        </div>}
      {ended.length > 0 && <div className="x78zum5 xdt5ytf x167g77z xeuugli">
          <button type="button" aria-expanded={earlierOpen} onClick={onToggleEarlier} {...stylex.props(styles.toggle, press.scale)}>
            <Icon icon={earlierOpen ? ChevronUp : ChevronDown} size={18} strokeWidth={2} />
            {earlierOpen ? copy.calendar.hideEarlier : copy.calendar.showEarlier(ended.length)}
          </button>
          {earlierOpen && <Rows rows={ended} shared={shared} />}
        </div>}
      {groupsOf(groups).map(group => <div key={group.key} className="x78zum5 xdt5ytf x167g77z xeuugli">
          {group.head && <span className="xaope02 xr0iwhn xif65rj xbzybt3 x1u7k74 xtvhhri">{group.head}</span>}
          <Rows rows={group.rows} shared={shared} />
        </div>)}
    </div>;
}
function messageFor(free: TimelineFree, count: number): string | undefined {
  if (free === "none") return copy.calendar.nobodySelected;
  return count === 0 ? copy.calendar.nothingScheduled : undefined;
}
function groupsOf(groups: DayListGroups<CalendarEvent>): Group[] {
  if (groups.kind === "day") {
    const head = groups.count > 0 ? copy.calendar.eventCount(groups.count) : undefined;
    return groups.rows.length > 0 ? [{
      key: "day",
      head,
      rows: groups.rows
    }] : [];
  }
  return [{
    key: "now",
    head: copy.calendar.happeningNow,
    rows: groups.now
  }, {
    key: "later",
    head: copy.calendar.laterToday(groups.laterCount),
    rows: groups.later
  }].filter(group => group.rows.length > 0);
}
function Rows({
  rows,
  shared
}: {
  rows: readonly DayListRow<CalendarEvent>[];
  shared: Shared;
}) {
  return rows.map(row => row.kind === "plan" ? <PlanRow key={row.plan.id} row={row} shared={shared} /> : <FreeRow key={`free-${row.start}`} row={row} shared={shared} />);
}
function AllDayRow({
  event,
  tags,
  onOpen
}: {
  event: CalendarEvent;
  tags: readonly Tag[];
  onOpen: (event: CalendarEvent) => void;
}) {
  return <button type="button" onClick={() => onOpen(event)} {...stylex.props(styles.tile, styles.allDay, press.scale)}>
      <span className="x2lah0s xr0iwhn x4z9k3i xbzybt3 xtvhhri xuxw1ft">{copy.events.allDay}</span>
      <PersonDot color={eventColor(event, tags)} size="sm" />
      <span className="x78zum5 xdt5ytf x195vfkc xeuugli">
        <span className="x1j61zf2 xfbjfs5 x1yrajzq x132q4wb x1fzhlzt">{event.title}</span>
        {event.location && <span className="xif65rj x15i68ds xuxw1ft xb3r6kr xlyipyv xeuugli">{event.location}</span>}
      </span>
    </button>;
}
function PlanRow({
  row,
  shared
}: {
  row: Extract<DayListRow<CalendarEvent>, {
    kind: "plan";
  }>;
  shared: Shared;
}) {
  const {
    plan: event,
    status
  } = row;
  const past = status === "past";
  const time = rangeText(row.start, row.end, shared.clock);
  const chip = past ? 0 : planRain(row.start, row.end ?? row.start + OPEN_PLAN_LENGTH, shared.rain, UNBOUNDED);
  const note = past ? "" : firstLine(event.notes);
  const meta = [whoLabel(event, shared.tags, copy.events.everyone), event.location].filter(Boolean).join(" · ");
  return <button type="button" aria-label={copy.timeline.planName(event.title, time)} onClick={() => shared.onOpenEvent(event)} {...stylex.props(styles.tile, styles.plan, past ? styles.past : fillOf(event, shared.tags), status === "now" && styles.now, press.scale)}>
      <span className="x78zum5 x6s0dn4 x1qughib x167g77z xeuugli">
        <span className="xr0iwhn xif65rj xcheg63 xdod15v xss6m8b xuxw1ft xb3r6kr xlyipyv xeuugli">{time}</span>
        <PersonDots event={event} tags={shared.tags} />
      </span>
      <span {...{
      0: {
        className: "x19d36u7 xfbjfs5 x1yrajzq xdod15v x1fzhlzt x104kibb x1h7i4cw x1ua5tub xb3r6kr"
      },
      1: {
        className: "x19d36u7 x1yrajzq xdod15v x1fzhlzt x104kibb x1h7i4cw x1ua5tub xb3r6kr x1v94iar"
      }
    }[!!past << 0]}>{event.title}</span>
      {meta && <span className="x1jvydc1 x15i68ds xwn7fz2 xuxw1ft xb3r6kr xlyipyv xeuugli">{meta}</span>}
      {(chip > 0 || note !== "") && <span className="x78zum5 x6s0dn4 x167g77z xeuugli x1nn3v0j">
          {chip > 0 && <span className="x2lah0s x78zum5 x6s0dn4 x1jnr06f xqhrb7l xf4egzd xmzvs34 x2vl965 xh540kj xr0iwhn x4z9k3i xcheg63 xuxw1ft">
              <Icon icon={CloudRain} size={16} />
              {copy.timeline.rain(chip)}
            </span>}
          {note !== "" && <span className="x78zum5 x6s0dn4 x17d4w8g xeuugli x1jvydc1 x1cf7cre xwn7fz2">
              <span className="x78zum5 x2lah0s">
                <Icon icon={FileText} size={16} />
              </span>
              <span className="xuxw1ft xb3r6kr xlyipyv xeuugli">{note}</span>
            </span>}
        </span>}
    </button>;
}
function FreeRow({
  row,
  shared
}: {
  row: Extract<DayListRow<CalendarEvent>, {
    kind: "free";
  }>;
  shared: Shared;
}) {
  const {
    day,
    tz,
    clock
  } = shared;
  const label = copy.timeline.free(shared.free === "everyone", formatDuration(row.end - row.start));
  return <button type="button" aria-label={copy.timeline.freeName(rangeText(row.start, row.end, clock))} onClick={() => shared.onAddEvent({
    start: atMinute(day, row.newEvent.start, tz),
    end: atMinute(day, row.newEvent.end, tz)
  })} {...stylex.props(styles.tile, styles.free, press.scale)}>
      <span className="x78zum5 x1a02dak xfex06f xeuugli x1vvogim xr0iwhn x4z9k3i xbzybt3 xd4r4e8 xtvhhri">
        <span className="xuxw1ft">{formatClock(row.start, clock)}</span>
        <span className="xuxw1ft">{label}</span>
      </span>
      <Icon icon={Plus} size={18} strokeWidth={2} />
    </button>;
}
function PersonDots({
  event,
  tags
}: {
  event: CalendarEvent;
  tags: readonly Tag[];
}) {
  if (event.everyone || event.tagIds.length < 2) return null;
  const on = tags.filter(tag => event.tagIds.includes(tag.id)).slice(0, DOTS_MAX);
  return <span className="x78zum5 x1ed6fcf x2lah0s">
      {on.map(tag => <PersonDot key={tag.id} color={tag.color} size="xsm" />)}
    </span>;
}
function fillOf(event: CalendarEvent, tags: readonly Tag[]) {
  const color = eventColor(event, tags);
  return color === "soft" ? styles.soft : tagFills[color];
}
function rainByHour({
  hourly
}: TimelineWeather, day: string, tz: string): Map<number, number> {
  return new Map([...hourlyByHour(hourly, day, tz)].map(([hour, point]) => [hour, point.precip ?? 0]));
}
function rangeText(start: number, end: number | undefined, clock: Clock): string {
  if (end === undefined) return formatClock(start, clock);
  const {
    time,
    meridiem
  } = rangeParts(start, end, clock);
  return meridiem ? `${time} ${meridiem}` : time;
}
function firstLine(notes: string | undefined): string {
  return notes?.trim().split("\n")[0]?.trim() ?? "";
}